import { find, flatten, map, max, reduce, sortBy } from 'lodash-es';

import { savings } from './shared-calculations';
import {
  isDiscontinued, isDiscontinuedCrossed, isNotDiscontinuedCrossed,
  isLocked, isSelfCrossed, isUnpriced, isUnpricedCrossed, isExcluded,
  isIncumbentSelfCross,
} from './stateLocators';
import { Tier } from '../../../api/api.interfaces';

function getSavings(product: any) {
  let savingsAmount = savings(
    product.calculations.continuedSpend,
    product.calculations.plannedSpend,
  );

  if (savingsAmount > 1) {
    return '+';
  } else if (savingsAmount < -1) {
    return '-';
  }

  return '';
}

// Functions to manipulate a specific product (and it's crosses).
export function updateAllocations(product: any, allocations: any[]) {
  // If the incumbent product is part of an offer, we shouldn't assign allocation to the incumbent directly.
  let selfCrossed = isSelfCrossed(product);

  let incumbentAllocation = selfCrossed
    ? 0
    : find(allocations, { productId: product.incumbentProduct.productId })
      .allocationPct;

  product.incumbentProduct.allocation = incumbentAllocation;
  product.crossedProductsByOffer.forEach((offer: any) => {
    offer.crossedProducts.forEach((product: any) => {
      // If this product is in our list of allocations, set it's allocation percentage.
      product.allocation = find(allocations, {
        productId: product.productId,
      }).allocationPct;
    });
  });
}

// Converts the data we get from the API into the form we need for Angular UI
// Grid.
export function productsToModel(products: any[], offers: any[]) {
  // Mark the rows for "zebra striping" but not simple alternating. We want it to alternate by incumbent product.
  // So those incumbents which have several rows are all clustered under the same color.
  let currentClass = 'odd';

  // One product in the source data maps to one or more rows of data for Angular UI Grid. The flatten is there to
  // deal with those situations where one product maps to an array of multiple rows. So the array which might
  // contain further arrays gets flattened into one single list of rows.
  return flatten(
    products.map((product, productIndex) => {
      let productRows: any[] = [];
      let lockedAllocation = isLocked(product);
      let discontinued = isDiscontinued(product);
      let unpriced = isUnpriced(product);
      let unpricedCrossed = isUnpricedCrossed(product);
      let discontinuedCrossed = isDiscontinuedCrossed(product);
      let notDiscontinuedCrossed = isNotDiscontinuedCrossed(product);
      let excluded = isExcluded(product);
      let incumbentSelfCross = isIncumbentSelfCross(product);

      let aggregateValues = {
        crossAssessments: new Set(),
        formularyPricingType: new Set(),
        numberOfAllocations: 0,
        sellersWithCrosses: new Set(),
        totalAllocation: product.incumbentProduct.allocation,
        variance: 0,
        crossType: new Set(),
      };
      let foundSelfCross = false;

      // Create an array listing how many crossed products there are for each offer. For example, [ 2, 1 ] says that
      // the first offer has two crossed products and the second offer only one.
      let countsOfCrossedProducts = map(
        product.crossedProductsByOffer,
        (offer: any) => {
          aggregateValues = reduce(
            offer.crossedProducts,
            (aggregate: any, crossedProduct: any) => {
              // Figure out if any of these products is a self cross.
              if (crossedProduct.crossType === 'SelfCross') {
                foundSelfCross = true;
              }

              let sellerId = offers.find((item) => item.id === offer.offerId)
                .seller.id;

              // Figure out how much the incumbent price varies from the price of this product.
              // That amount is the variance.
              let current = product.incumbentProduct.unitPrice;
              let target = crossedProduct.unitPrice;
              let variance =
                crossedProduct.unitPrice == null
                  ? 0
                  : Math.abs(((current - target) / current) * 100.0);

              // We mark each crossed product with the incumbent product it corresponds to (helpful for a lot of the
              // other code).
              crossedProduct.incumbentProductId =
                product.incumbentProduct.productId;

              // We also mark a given cross if it does not have any pricing info.
              crossedProduct.pricingRequired = crossedProduct.unitPrice == null;

              return {
                crossAssessments: aggregate.crossAssessments.add(
                  crossedProduct.crossOpinion,
                ),
                crossType: aggregate.crossType.add(
                  crossedProduct.crossTypeDesc?.trim().replace('-', '').
                    replace(/\s/g, "").toLowerCase(),
                ),
                formularyPricingType: aggregate.formularyPricingType.add(
                  product.formularyPricingType,
                ),
                numberOfAllocations:
                  aggregate.numberOfAllocations +
                  (crossedProduct.allocation > 0 ? 1 : 0),
                sellersWithCrosses: aggregate.sellersWithCrosses.add(sellerId),
                totalAllocation:
                  aggregate.totalAllocation + crossedProduct.allocation,
                variance: Math.max(aggregate.variance, variance),
              };
            },
            aggregateValues,
          );

          return offer.crossedProducts.length;
        },
      );

      product.calculations.numberOfAllocations =
        aggregateValues.numberOfAllocations +
        (product.incumbentProduct.allocation > 0 ? 1 : 0);
      product.calculations.totalAllocation = aggregateValues.totalAllocation;
      product.calculations.variance = aggregateValues.variance;

      product.visible = false;
      product.discontinued = discontinued;
      product.unpriced = unpriced;
      product.discontinuedCrossed = discontinuedCrossed;
      product.unpricedCrossed = unpricedCrossed;
      product.notDiscontinuedCrossed = notDiscontinuedCrossed;
      product.excluded = excluded;
      product.incumbentSelfCross = incumbentSelfCross;

      // Add one to the array because we're always going to have one row even if all we have on it is the incumbent
      // product.
      countsOfCrossedProducts.push(1);

      // We will iterate through the number of times that is the max of (one and the count of crossed products for
      // each offer ID).
      // Example:
      //   If one of the offers has only one cross, another has no crosses, and a third offer has
      //   three crosses, we will generate three output rows because that is how many we will
      //   need to show one of the offers. The other two offers need fewer rows, but we always
      //   have to accommodate the max.
      let numGeneratedRows = max(countsOfCrossedProducts);

      for (let index = 0; index < numGeneratedRows; index++) {
        let lastRow = index === numGeneratedRows - 1;

        // Every row only gets the alternative products from different sellers.
        let row = {
          product,
          productIndex,
          currentClass,

          get class() {
            return `${this.currentClass}${lastRow ? ' lastRow' : ''}`;
          },
          get crossAssessments() {
            return Array.from(aggregateValues.crossAssessments);
          },
          get crossType() {
            return Array.from(aggregateValues.crossType);
          },
          get incumbentSelfCross() {
            return this.product.incumbentSelfCross;

          },
          get formularyPricingType() {
            return Array.from(aggregateValues.formularyPricingType);
          },
          get exclude() {
            return this.product.calculations.excludedPlannedSpend >= 0 && this.product.excluded;
          },
          // Incumbent only comes back on the first row. Every other row doesn't see it.
          get incumbent() {
            return index === 0 ? this.product.incumbentProduct : null;
          },
          get incumbentProductId() {
            return this.product.incumbentProduct.productId;
          },
          get incumbentSellerId() {
            return this.product.incumbentProduct.seller.id;
          },
          get incumbentUnspscId() {
            return this.product.incumbentProduct.unspsc.id;
          },
          get lastRow() {
            return lastRow; // Only the last row in the UI shows totals.
          },
          get lockedAllocation() {
            return lockedAllocation;
          },
          get sortCatalogNumber() {
            return this.product.incumbentProduct.catalogNumber;
          },
          get sortPlannedSpend() {
            return this.product.calculations.plannedSpend;
          },
          get sortSellerName() {
            return this.product.incumbentProduct.seller.name;
          },
          get savings() {
            return getSavings(this.product);
          },
          get sellersWithCrosses() {
            return Array.from(aggregateValues.sellersWithCrosses);
          },
          get sortIndex() {
            return index;
          },
          get totalAllocation() {
            return this.product.calculations.totalAllocation;
          },
          get totals() {
            return this.product.calculations;
          },
          get variance() {
            return this.product.calculations.variance;
          },
          get visible() {
            return this.product.visible;
          },
          get discontinued() {
            return this.product.discontinued;
          },

          get discontinuedCrosses() {
            return this.product.discontinuedCrossed;
          },

          get notDiscontinuedCrosses() {
            return this.product.notDiscontinuedCrossed;
          },

          get unpriced() {
            return this.product.unpriced;
          },

          get unpricedCrosses() {
            return this.product.unpricedCrossed;
          },



        };

        product.incumbentProduct.selfCross = foundSelfCross;

        // Each individual cross for this row is getting assigned to a member value of the row with the
        // key being the offer ID. The reason for that is that it makes it really easy to find the right
        // product to show in each column for each row because we can simply ask for the product using
        // the offer ID (which we know for each column) as the key. This could have been done other ways,
        // but it's really just another reference to a given product and not a copy, so the cost is low
        // and the simplicity is high.
        product.crossedProductsByOffer.forEach((offer: any) => {
          if (index < offer.crossedProducts.length) {
            let crossedProduct = offer.crossedProducts[index];

            row[offer.offerId] = crossedProduct;
          }
        });

        productRows.push(row);
      }

      // After each product, swap between odd and even.
      currentClass = currentClass === 'odd' ? 'even' : 'odd';

      return productRows;
    }),
  );
}

export function mapTiers(tiers: []) {
  return sortBy(
    map(tiers, (tier: Tier) => {
      return {
        tier: {
          id: tier.id,
          name: tier.name,
        },
        tierType: tier.tierType,
        targetSpendLevel: {
          type: {
            name: tier.spendLevelType.name,
            displayName: tier.spendLevelType.displayName,
            fieldNames: tier.spendLevelType.fieldNames,
            operator: tier.spendLevelType.operator
          },
          dollarMarketShare: tier.dollarMarketShare,
          unitMarketShare: tier.unitMarketShare,
          absoluteDollars: tier.absoluteDollars,
          absoluteUnits: tier.absoluteUnits,

        },
        attainability: tier.attainability,
        order: tier.order,
      }
    })
    , ['order']);
}
